import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CarCard } from '../components/CarCard';
import { Button } from '../components/ui/button';
import { ArrowRight, Globe, Ship, Shield, Zap, Handshake } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { t } = useLanguage();
  const [featuredCars, setFeaturedCars] = useState([]);
  const [impressions, setImpressions] = useState([]);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await axios.get(`${API}/cars?featured=true&limit=4`);
        setFeaturedCars(res.data.cars || []);
      } catch (err) {
        console.error('Failed to fetch featured cars:', err);
      }
    };
    const fetchImpressions = async () => {
      try {
        const res = await axios.get(`${API}/impressions`);
        setImpressions(res.data.impressions || []);
      } catch (err) {
        console.error('Failed to fetch impressions:', err);
      }
    };
    fetchFeatured();
    fetchImpressions();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - Tetris Grid */}
      <section className="pt-20 md:pt-0 min-h-screen flex items-center relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_global-auto-export-2/artifacts/tz4y1q95_Hero%20background%20P4C.png"
            alt="Partners4Cars Porsche"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center min-h-[80vh]">
            {/* Left Content */}
            <div className="md:col-span-7 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] mb-4 block">
                  Partners4Cars
                </span>
                <h1 className="text-5xl md:text-7xl font-black tracking-tight uppercase text-white font-[Chivo] leading-[0.95]">
                  {t('hero.title')}
                </h1>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-lg md:text-xl text-zinc-400 max-w-lg leading-relaxed"
              >
                {t('hero.subtitle')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/inventory">
                  <Button
                    className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider px-8 h-12 text-sm"
                    data-testid="hero-cta-inventory"
                  >
                    {t('hero.cta')}
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white hover:text-black rounded-sm uppercase tracking-wider px-8 h-12 text-sm"
                    data-testid="hero-cta-contact"
                  >
                    {t('hero.ctaSecondary')}
                  </Button>
                </Link>
              </motion.div>
            </div>

            {/* Right Stats Block */}
            <div className="md:col-span-5 hidden md:block">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="space-y-4"
              >
                {[
                  { value: t('hero.stat1Value'), label: t('hero.stat1Label') },
                  { value: t('hero.stat2Value'), label: t('hero.stat2Label') },
                  { value: t('hero.stat3Value'), label: t('hero.stat3Label') },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-sm p-6 flex items-center justify-between"
                  >
                    <span className="text-3xl md:text-4xl font-black text-white font-[Chivo]">
                      {stat.value}
                    </span>
                    <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 text-right">
                      {stat.label}
                    </span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      {featuredCars.length > 0 && (
        <section className="py-24 md:py-32" data-testid="featured-section">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block mb-3">
                  {t('inventory.featured')}
                </span>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white font-[Chivo]">
                  {t('nav.inventory')}
                </h2>
              </div>
              <Link
                to="/inventory"
                className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-white uppercase tracking-widest transition-colors"
                data-testid="view-all-inventory-link"
              >
                {t('inventory.viewDetails')}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCars.slice(0, 4).map((car) => (
                <CarCard key={car.id} car={car} />
              ))}
            </div>

            <div className="md:hidden mt-8 text-center">
              <Link to="/inventory">
                <Button
                  className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider px-8 h-12 text-sm"
                  data-testid="mobile-view-all-btn"
                >
                  {t('inventory.viewDetails')}
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Section 1: Why Choose Partners4Cars */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="wholesale-partner-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block">
                  {t('wholesale.eyebrow')}
                </span>
                <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white font-[Chivo] leading-tight">
                  {t('wholesale.title')}
                </h2>
                <div className="space-y-4 text-zinc-400 text-sm md:text-base leading-relaxed">
                  <p>
                    {t('wholesale.p1')}
                  </p>
                  <p>
                    {t('wholesale.p2')}
                  </p>
                  <p>
                    {t('wholesale.p3')}
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 pt-4">
                  <Link to="/inventory">
                    <Button
                      className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider px-8 h-12 text-sm"
                      data-testid="section1-inventory-btn"
                    >
                      {t('wholesale.ctaInventory')}
                      <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white hover:text-black rounded-sm uppercase tracking-wider px-8 h-12 text-sm"
                      data-testid="section1-contact-btn"
                    >
                      {t('wholesale.ctaContact')}
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-[#121212] border border-zinc-800/50 rounded-md p-8 md:p-10 space-y-6"
              >
                {[
                  { icon: Globe, label: t('wholesale.feature1Title'), desc: t('wholesale.feature1Desc') },
                  { icon: Zap, label: t('wholesale.feature2Title'), desc: t('wholesale.feature2Desc') },
                  { icon: Handshake, label: t('wholesale.feature3Title'), desc: t('wholesale.feature3Desc') },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#ED2227]/10 rounded-sm flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-[#ED2227]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm font-[Chivo] mb-1">{item.label}</h4>
                      <p className="text-zinc-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: 25+ Years Experience */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="experience-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: t('experience.stat1Value'), label: t('experience.stat1Label') },
                    { value: t('experience.stat2Value'), label: t('experience.stat2Label') },
                    { value: t('experience.stat3Value'), label: t('experience.stat3Label') },
                    { value: t('experience.stat4Value'), label: t('experience.stat4Label') },
                  ].map((stat, i) => (
                    <div
                      key={i}
                      className="bg-[#121212] border border-zinc-800/50 rounded-md p-6 text-center"
                    >
                      <span className="text-3xl md:text-4xl font-black text-[#ED2227] font-[Chivo] block mb-2">
                        {stat.value}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 leading-tight block">
                        {stat.label}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block">
                  {t('experience.eyebrow')}
                </span>
                <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white font-[Chivo] leading-tight">
                  {t('experience.title')}
                </h2>
                <div className="space-y-4 text-zinc-400 text-sm md:text-base leading-relaxed">
                  <p>
                    {t('experience.p1')}
                  </p>
                  <p>
                    {t('experience.p2')}
                  </p>
                </div>
                <ul className="space-y-3 pt-2">
                  {[
                    t('experience.bullet1'),
                    t('experience.bullet2'),
                    t('experience.bullet3'),
                    t('experience.bullet4'),
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-zinc-300 text-sm">
                      <div className="w-1.5 h-1.5 bg-[#ED2227] rounded-full shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
                  {t('experience.closing')}
                </p>
                <div className="pt-4">
                  <Link to="/about">
                    <Button
                      className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider px-8 h-12 text-sm"
                      data-testid="section2-about-btn"
                    >
                      {t('experience.ctaAbout')}
                      <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - 3 USPs */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="why-us-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block mb-3">
              Partners4Cars
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white font-[Chivo]">
              {t('about.whyUs')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Globe, title: t('about.reason1Title'), text: t('about.reason1Text') },
              { icon: Ship, title: t('about.reason2Title'), text: t('about.reason2Text') },
              { icon: Shield, title: t('about.reason3Title'), text: t('about.reason3Text') },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="bg-[#121212] border border-zinc-800/50 rounded-md p-8 md:p-10 group hover:border-[#ED2227]/20 transition-all duration-300"
              >
                <item.icon className="w-8 h-8 text-[#ED2227] mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-bold text-white mb-3 font-[Chivo]">{item.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio / Our Work */}
      {impressions.length > 0 && (
        <section className="py-24 md:py-32 border-t border-white/5" data-testid="home-impressions-section">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block mb-3">
                  Portfolio
                </span>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white font-[Chivo]">
                  {t('impressions.title')}
                </h2>
              </div>
              <Link
                to="/impressions"
                className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-white uppercase tracking-widest transition-colors"
                data-testid="view-all-impressions-link"
              >
                {t('inventory.viewDetails')}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impressions.slice(0, 3).map((imp, i) => (
                <motion.div
                  key={imp.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                >
                  <Link
                    to="/impressions"
                    className="group block bg-[#121212] border border-zinc-800/50 rounded-md overflow-hidden hover:border-[#ED2227]/20 transition-all duration-300"
                    data-testid={`home-impression-${i}`}
                  >
                    {imp.images?.[0] && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={imp.images[0]}
                          alt={imp.title}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#ED2227] block mb-2">
                        {imp.category}
                      </span>
                      <h3 className="text-lg font-bold text-white font-[Chivo] mb-2">{imp.title}</h3>
                      <p className="text-sm text-zinc-500 leading-relaxed line-clamp-2">{imp.description}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="md:hidden mt-8 text-center">
              <Link to="/impressions">
                <Button
                  className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider px-8 h-12 text-sm"
                  data-testid="mobile-view-all-impressions-btn"
                >
                  {t('inventory.viewDetails')}
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
